import { defineStore } from 'pinia';
import { BalanceBucketDropdownOptions } from '@stats/ar/constants';
import { roundWithPrecision } from '@/utils/formatting';
import { arStatsApi } from '@/services/arStatsService';

export const useArStatsStore = defineStore('arStats', {
  state: () => ({
    rowLimit: 250,
    currentPage: 1,
    searchDirection: 'asc',
    searchField: 'rev_rsn',
    totalRows: 0,

    arDetailsData: [],
    arDetailsDate: {
      value: `${new Date().getFullYear()}-${String(
        new Date().getMonth() + 1
      ).padStart(2, '0')}`,
      label: 'Current',
    },

    breakoutStartMonth: (() => {
      const today = new Date();
      const sixMonthsAgo = new Date();
      // Set day first so e.g. Sep 30 doesn't roll Feb 30 over into March.
      sixMonthsAgo.setDate(1);
      sixMonthsAgo.setMonth(today.getMonth() - 7);
      return {
        value: `${sixMonthsAgo.getFullYear()}-${String(
          sixMonthsAgo.getMonth() + 1
        ).padStart(2, '0')}`,
        label: sixMonthsAgo.toLocaleString('default', {
          month: 'long',
          year: 'numeric',
        }),
      };
    })(),
    breakoutEndMonth: {
      value: `${new Date().getFullYear()}-${String(
        new Date().getMonth() + 1
      ).padStart(2, '0')}`,
      label: 'Current',
    },

    lastUpdated: '',

    currentARCount: 0,
    currentARBalance: 0,
    percentGreaterThan90: 0,
    percentGreaterThan180: 0,
    percentGreaterThan365: 0,

    //leftmost trend
    previousMonthTrendTotal: 0,
    previousMonth: '',

    //middle left trend
    dateRangeAverage: 0,
    sixMonthDateRange: '',

    //middle right trend
    monthVsSixMonthAverageCurrency: 0,
    percentDiffCurrency: 0,
    monthCount: 0,
    percentDiffCurrencyArrowTernary: 0,
    percentDiffCurrencyColorTernary: 0,

    //rightmost trend
    monthVsSixMonthAverageAgain: 0,
    percentDiff: 0,
    percentDiffCountArrowTernary: 0,
    percentDiffCountColorTernary: 0,

    highDollarOverview: [
      {
        name: 'Hospital High Dollar',
        value: 50000,
        type: 'currency',
      },
      {
        name: 'Physician High Dollar',
        value: 30000,
        type: 'currency',
      },
      {
        name: 'Number Of Days From Worked',
        value: 30,
      },
      {
        name: 'Number Of Days From Service',
        value: 30,
      },
      {
        name: 'Number Of Days From Submission',
        value: 30,
      },
    ],

    highDollarCards: {
      total: {
        cardTitle: 'Total',
        cardColorTernary: 0,
        displayedValues: { count: 0, balance: 0, percentage: 0 },
      },
      recentlyBilled: {
        cardTitle: 'Recently Billed',
        cardColorTernary: 1,
        displayedValues: {
          count: 0,
          balance: 0,
          percentage: 0,
        },
      },
      recentlyWorked: {
        cardTitle: 'Recently Worked',
        cardColorTernary: 2,
        displayedValues: { count: 0, balance: 0, percentage: 0 },
      },
      needsAttention: {
        cardTitle: 'Needs Attention',
        cardColorTernary: 3,
        displayedValues: { count: 0, balance: 0, percentage: 0 },
      },
    },

    //Filters
    filtersApplied: [],

    selectedBillingType: '',
    selectedBillingProviderList: [],
    selectedBillingEntityList: [],
    selectedUserList: [],
    selectedLocationList: [],
    selectedDepartmentList: [],
    selectedFinancialClassList: [],
    selectedPayerPlanList: [],
    selectedPayerList: [],
    selectedPlanList: [],
    selectedPhaseList: [],
    selectedStatusList: [],
    selectedReasonList: [],
    selectedBalanceBucketList: [],
    selectedAgeCategoryList: [],
    selectedActivityTypeCodeList: [],
    selectedLastActivityTypeList: [],
    selectedLastActivityCodeList: [],

    //For modal
    balanceBucketOptions: BalanceBucketDropdownOptions,
    lastActivityTypeCodeOptions: [],

    //high dollar thresholds
    hospitalHighDollarThreshold: 50000,
    physicianHighDollarThreshold: 3000,
    numberOfDaysFromWorkedThreshold: 30,
    numberOfDaysFromServiceThreshold: 30,
    numberOfDaysFromSubmissionThreshold: 30,

    //total breakout chart
    splitBy: { id: 'none', text: 'None' },
    totalBreakoutChart: {
      categories: [],
      series: [],
      sortableSeries: [],
      totalRows: 0,
    },

    //high dollar chart
    totalARHeaders: [],
    totalBreakoutRowLimit: 250,
    totalBreakoutCurrentPage: 1,
    totalBreakoutSearchDirection: 'asc',
    totalBreakoutSearchField: 'some_category',

    //Current AR breakout chart
    highDollarSplitBy: 'financialClass',
    highDollarBreakoutChart: {
      categories: [],
      series: [],
      sortableSeries: [],
      totalRows: 0,
    },
    highDollarBreakoutRowLimit: 250,
    highDollarBreakoutCurrentPage: 1,
    highDollarBreakoutSearchDirection: 'asc',
    highDollarBreakoutSearchField: 'rev_rsn',
    highDollarBreakoutStatus: 'needs attention',

    sortColumnMapping: {
      rsn: 'rev_rsn',
      balance_bucket: 'balance_bucket_key',
    },

    lastFetchFingerprint: null,
  }),
  getters: {
    getFiltersApplied: state => {
      return state.filtersApplied;
    },
  },
  actions: {
    async applyFilters(
      startDate,
      endDate,
      billingType,
      billingProviderList,
      billingEntityList,
      userList,
      locationList,
      departmentList,
      financialClassList,
      payerPlanList,
      payerList,
      planList,
      phaseList,
      statusList,
      reasonList,
      balanceBucketList,
      ageCategoryList,
      lastActivityTypeList,
      lastActivityCodeList
    ) {
      const incomingFingerprint = JSON.stringify([
        startDate,
        endDate,
        billingType,
        billingProviderList,
        billingEntityList,
        userList,
        locationList,
        departmentList,
        financialClassList,
        payerPlanList,
        payerList,
        planList,
        phaseList,
        statusList,
        reasonList,
        balanceBucketList,
        ageCategoryList,
        lastActivityTypeList,
        lastActivityCodeList,
      ]);

      if (incomingFingerprint === this.lastFetchFingerprint) {
        return;
      }

      // Make clean copies of everything to break object references
      this.startDate = startDate;
      this.endDate = endDate;
      this.selectedBillingType = billingType;
      this.selectedBillingProviderList = Array.isArray(billingProviderList)
        ? [...billingProviderList]
        : [];
      this.selectedBillingEntityList = Array.isArray(billingEntityList)
        ? [...billingEntityList]
        : [];
      this.selectedUserList = Array.isArray(userList) ? [...userList] : [];
      this.selectedLocationList = Array.isArray(locationList)
        ? [...locationList]
        : [];
      this.selectedDepartmentList = Array.isArray(departmentList)
        ? [...departmentList]
        : [];
      this.selectedFinancialClassList = Array.isArray(financialClassList)
        ? [...financialClassList]
        : [];
      this.selectedPayerPlanList = Array.isArray(payerPlanList)
        ? [...payerPlanList]
        : [];
      this.selectedPayerList = Array.isArray(payerList) ? [...payerList] : [];
      this.selectedPlanList = Array.isArray(planList) ? [...planList] : [];
      this.selectedPhaseList = Array.isArray(phaseList) ? [...phaseList] : [];
      this.selectedStatusList = Array.isArray(statusList)
        ? [...statusList]
        : [];
      this.selectedReasonList = Array.isArray(reasonList)
        ? [...reasonList]
        : [];
      this.selectedBalanceBucketList = Array.isArray(balanceBucketList)
        ? [...balanceBucketList]
        : [];
      this.selectedAgeCategoryList = Array.isArray(ageCategoryList)
        ? [...ageCategoryList]
        : [];
      this.selectedLastActivityTypeList = Array.isArray(lastActivityTypeList)
        ? [...lastActivityTypeList]
        : [];
      this.selectedLastActivityCodeList = Array.isArray(lastActivityCodeList)
        ? [...lastActivityCodeList]
        : [];

      // Reset pagination to page 1 when filters are applied
      this.totalBreakoutCurrentPage = 1;

      this.updateAppliedFilters();
      await Promise.allSettled([
        this.fetchARDetailsData(),
        this.fetchARInsights(),
        this.fetchTotalBreakoutChart(),
        this.fetchTotalBreakoutTable(),
        this.fetchHighDollarBreakoutChart(),
        this.fetchHighDollarBreakoutTable(),
        this.fetchLastUpdated(),
      ]);

      this.lastFetchFingerprint = incomingFingerprint;
    },
    updateAppliedFilters() {
      this.filtersApplied = [];

      if (this.selectedBillingType && this.selectedBillingType !== 'Show All') {
        this.filtersApplied.push('Billing Type Applied');
      }
      if (this.selectedBillingProviderList?.length) {
        this.filtersApplied.push('Billing Provider Applied');
      }
      if (this.selectedUserList?.length) {
        this.filtersApplied.push('User Applied');
      }
      if (this.selectedBillingEntityList?.length) {
        this.filtersApplied.push('Billing Entity Applied');
      }
      if (this.selectedLocationList?.length) {
        this.filtersApplied.push('Location Applied');
      }
      if (this.selectedDepartmentList?.length) {
        this.filtersApplied.push('Department Applied');
      }
      if (this.selectedFinancialClassList?.length) {
        this.filtersApplied.push('Financial Class Applied');
      }
      if (this.selectedPayerPlanList?.length) {
        this.filtersApplied.push('Payer Plan Applied');
      }
      if (this.selectedPayerList?.length) {
        this.filtersApplied.push('Payer Applied');
      }
      if (this.selectedPlanList?.length) {
        this.filtersApplied.push('Plan Applied');
      }
      if (this.selectedPhaseList?.length) {
        this.filtersApplied.push('Phase Applied');
      }
      if (this.selectedStatusList?.length) {
        this.filtersApplied.push('Status Applied');
      }
      if (this.selectedReasonList?.length) {
        this.filtersApplied.push('Reason Applied');
      }
      if (this.selectedBalanceBucketList?.length) {
        this.filtersApplied.push('Balance Bucket Applied');
      }
      if (this.selectedAgeCategoryList?.length) {
        this.filtersApplied.push('Age Category Applied');
      }
      if (this.selectedActivityTypeCodeList?.length) {
        this.filtersApplied.push('Activity Type Code Applied');
      }
      if (this.selectedLastActivityTypeList?.length) {
        this.filtersApplied.push('Activity Type Applied');
      }
      if (this.selectedLastActivityCodeList?.length) {
        this.filtersApplied.push('Activity Code Applied');
      }
    },
    resetFilters() {
      this.filtersApplied = [];
    },
    async fetchARDetailsData(
      page = 1,
      direction = this.searchDirection,
      field = this.searchField
    ) {
      //so on pagination results will persist
      this.currentPage = page;
      this.searchDirection = direction;
      this.searchField = field;

      const payload = this._buildPayload();
      const pagination = {
        pageNumber: page,
        pageLimit: this.rowLimit,
        sortField: this.sortColumnMapping[field] || field,
        sortOrder: direction,
      };
      payload.pagination = pagination;

      //We need to send the date in YYYY-MM-DD format
      //So we need to get the first day of the month
      payload.filter.date = this._getFirstDayOfMonth(this.arDetailsDate.value);

      const response = await arStatsApi.getDetails(payload);
      this.totalRows = response.total_records.total_count;
      this.arDetailsData = response.data;
    },
    async fetchARInsights() {
      const payload = this._buildPayload();
      await Promise.allSettled([
        this._fetchTotalAverageInsights(payload),
        this._fetchTotalCurrentSummaryInsights(payload),
        this.fetchHighDollarSummaryInsights(),
      ]);
    },
    async fetchLastUpdated() {
      const response = await arStatsApi.getLastUpdated();
      this.lastUpdated = response.last_updated_timestamp;
    },
    async fetchTotalBreakoutChart(splitBy = this.splitBy) {
      const payload = this._buildPayload();
      this.splitBy = splitBy;
      payload.splitBy = splitBy.id;
      payload.filter.startDate = this._getFirstDayOfMonth(
        this.breakoutStartMonth.value
      );
      const [year, month] = this.breakoutEndMonth.value.split('-').map(Number);

      // Calculate the last day of the current month
      // Creating a date with day 0 of next month gives the last day of current month
      const lastDay = new Date(year, month, 0).getDate();

      payload.filter.endDate = `${year}-${String(month).padStart(
        2,
        '0'
      )}-${String(lastDay).padStart(2, '0')}`;
      const response = await arStatsApi.getTotalBreakoutChart(payload);
      if (splitBy.id == 'user') {
        this.totalBreakoutChart.series = response.series.map(series => {
          return (series.name = series.first_name || 'None');
        });
      }
      this.totalBreakoutChart.categories = response.categories;
      this.totalBreakoutChart.series = response.series;
    },
    async fetchTotalBreakoutTable(
      splitBy = this.splitBy,
      page = this.totalBreakoutCurrentPage,
      limit = this.totalBreakoutRowLimit,
      direction = this.totalBreakoutSearchDirection,
      field = this.totalBreakoutSearchField
    ) {
      this.totalBreakoutCurrentPage = page;
      this.totalBreakoutSearchDirection = direction;
      this.totalBreakoutSearchField = field;
      this.totalBreakoutRowLimit = limit;
      if (splitBy.id == 'none') {
        return;
      }
      const payload = this._buildPayload();
      this.splitBy = splitBy;
      payload.splitBy = splitBy.id;
      payload.filter.startDate = this._getFirstDayOfMonth(
        this.breakoutStartMonth.value
      );
      const [year, month] = this.breakoutEndMonth.value.split('-').map(Number);

      // Calculate the last day of the current month
      // Creating a date with day 0 of next month gives the last day of current month
      const lastDay = new Date(year, month, 0).getDate();

      payload.filter.endDate = `${year}-${String(month).padStart(
        2,
        '0'
      )}-${String(lastDay).padStart(2, '0')}`;
      let secondSortField = '';
      let thirdSortField = '';

      // Add secondary and tertiary sort fields based on the split type
      if (
        (splitBy.id === 'payerPlan' || splitBy.id === 'activityTypeCode') &&
        field === 'some_category'
      ) {
        secondSortField = 'second_category';
      } else if (splitBy.id === 'phaseStatusReason') {
        if (field === 'some_category') {
          secondSortField = 'second_category';
          thirdSortField = 'third_category';
        } else if (field === 'second_category') {
          thirdSortField = 'third_category';
        }
      }

      const pagination = {
        pageNumber: page,
        pageLimit: limit,
        sortField: this.sortColumnMapping[field] || field,
        secondSortField,
        thirdSortField,
        sortOrder: direction,
      };
      payload.pagination = pagination;
      const response = await arStatsApi.getTotalBreakoutTable(payload);
      this.totalBreakoutChart.sortableSeries = response.sortableSeries;
      this.totalBreakoutChart.totalRows = response.totalRows;
      this.updateHeaders();
    },
    async fetchHighDollarBreakoutChart(splitBy = this.highDollarSplitBy) {
      const payload = this._buildPayload();
      this.highDollarSplitBy = splitBy;
      payload.splitBy = splitBy;

      const response = await arStatsApi.getHighDollarBreakoutChart(payload);
      this.highDollarBreakoutChart.categories = response.categories;
      this.highDollarBreakoutChart.series = response.series;
    },
    async fetchHighDollarBreakoutTable(
      page = this.highDollarBreakoutCurrentPage,
      status = this.highDollarBreakoutStatus,
      field = this.highDollarBreakoutSearchField,
      direction = this.highDollarBreakoutSearchDirection
    ) {
      this.highDollarBreakoutCurrentPage = page;
      this.highDollarBreakoutStatus = status;
      this.highDollarBreakoutSearchDirection = direction;
      this.highDollarBreakoutSearchField = field;

      const payload = this._buildPayload();
      const pagination = {
        pageNumber: page,
        pageLimit: this.highDollarBreakoutRowLimit,
        sortField: this.sortColumnMapping[field] || field,
        sortOrder: direction,
      };
      payload.pagination = pagination;
      payload.splitBy = '';
      payload.status = status;
      const response = await arStatsApi.getHighDollarBreakoutTable(payload);
      this.highDollarBreakoutChart.sortableSeries = response.sortableSeries;
      this.highDollarBreakoutChart.totalRows = response.totalRows;
    },
    updateHeaders() {
      // Start with the first column (always present)
      this.totalARHeaders = [
        {
          key: 'some_category',
          // If payerPlan, use "Payer" as the header, otherwise use the splitBy text
          value:
            this.splitBy.id === 'payerPlan'
              ? 'Payer'
              : this.splitBy.id === 'phaseStatusReason'
                ? 'Phase'
                : this.splitBy.id === 'activityTypeCode'
                  ? 'Last Activity Type'
                  : this.splitBy.text,
          sort: true,
        },
      ];

      // Only add second_category column for payerPlan
      if (this.splitBy.id === 'payerPlan') {
        this.totalARHeaders.push({
          key: 'second_category',
          value: 'Plan',
          sort: true,
        });
      } else if (this.splitBy.id === 'activityTypeCode') {
        this.totalARHeaders.push({
          key: 'second_category',
          value: 'Last Activity Code',
          sort: true,
        });
      } else if (this.splitBy.id === 'phaseStatusReason') {
        this.totalARHeaders.push({
          key: 'second_category',
          value: 'Status',
          sort: true,
        });
        this.totalARHeaders.push({
          key: 'third_category',
          value: 'Reason',
          sort: true,
        });
      }

      // Add remaining columns (month categories)
      this.totalARHeaders = [
        ...this.totalARHeaders,
        ...(this.totalBreakoutChart.categories || []).map(category => {
          const fullCategory = category.join(' ');
          return {
            key: fullCategory,
            value: fullCategory,
            format: 'currency',
            sort: true,
          };
        }),
      ];
    },
    async _fetchTotalAverageInsights(payload) {
      const response = await arStatsApi.getTotalAverageInsights(payload);
      //leftmost trend
      this.previousMonthTrendTotal = parseFloat(response.previous_month_amount);
      this.previousMonth = response.previous_month;

      //middle left trend
      this.dateRangeAverage = parseFloat(response.six_month_average_total);
      this.sixMonthDateRange = response.date_range;

      //middle right trend
      this.monthVsSixMonthAverageCurrency = parseFloat(
        response.dollar_difference
      );
      this.percentDiffCurrency = roundWithPrecision(
        response.dollar_difference_percent,
        1
      );
      this.monthCount = response.month_count;
      this.percentDiffCurrencyArrowTernary =
        response.dollar_difference > 0 ? 2 : 1;
      this.percentDiffCurrencyColorTernary =
        response.dollar_difference > 0 ? 1 : 2;

      //rightmost trend
      this.monthVsSixMonthAverageAgain = Math.round(response.count_difference);
      this.percentDiff = roundWithPrecision(
        response.count_percent_difference,
        1
      );
      this.percentDiffCountArrowTernary = response.count_difference > 0 ? 2 : 1;
      this.percentDiffCountColorTernary = response.count_difference > 0 ? 1 : 2;
    },
    async _fetchTotalCurrentSummaryInsights(payload) {
      const response = await arStatsApi.getCurrentSummaryInsights(payload);
      this.currentARCount = parseInt(response.total_records) || 0;
      this.currentARBalance = parseFloat(response.total_balance);
      this.percentGreaterThan90 = roundWithPrecision(
        response.percent_over_90_days,
        1
      );
      this.percentGreaterThan180 = roundWithPrecision(
        response.percent_over_180_days,
        1
      );
      this.percentGreaterThan365 = roundWithPrecision(
        response.percent_over_365_days,
        1
      );
    },
    async fetchHighDollarSummaryInsights() {
      const payload = this._buildPayload();
      // Reset all high dollar card values to zero first
      Object.keys(this.highDollarCards).forEach(cardKey => {
        this.highDollarCards[cardKey].displayedValues.balance = 0;
        this.highDollarCards[cardKey].displayedValues.count = 0;
        this.highDollarCards[cardKey].displayedValues.percentage = 0;
      });

      const response = await arStatsApi.getHighDollarSummaryInsights(payload);
      const statusMap = {
        'recently billed': 'recentlyBilled',
        'recently worked': 'recentlyWorked',
        'needs attention': 'needsAttention',
        'total high dollar': 'total',
      };
      response.map(highDollarStatus => {
        if (highDollarStatus.status == 'other high dollar') return;
        if (statusMap[highDollarStatus.status]) {
          this.highDollarCards[
            statusMap[highDollarStatus.status]
          ].displayedValues.balance = parseFloat(
            highDollarStatus.status_balance || 0
          );
          this.highDollarCards[
            statusMap[highDollarStatus.status]
          ].displayedValues.count = highDollarStatus.record_count;
          this.highDollarCards[
            statusMap[highDollarStatus.status]
          ].displayedValues.percentage = roundWithPrecision(
            highDollarStatus.percentage,
            1
          );
        }
      });
      // Reset these values too
      this.highDollarCount = response.high_dollar_record_count || 0;
      this.highDollarAmount = parseFloat(response.high_dollar_balance_sum || 0);
      this.highDollarPercentage = Math.round(
        response.percent_records_high_dollar || 0
      );
    },
    get_pagination() {
      return {
        sortField: this.searchField,
        sortOrder: this.searchDirection,
      };
    },
    build_export_payload() {
      //We need to send the date in YYYY-MM-DD format
      // to get today's AR details for export
      //So we need to get the first day of the month

      const date = this._getFirstDayOfMonth(this.arDetailsDate.value);
      // We can't just call build payload because the AR date filters
      // are different on each AR tab
      return { ...this._buildPayload().filter, date };
    },
    _getFirstDayOfMonth(dateStr) {
      return dateStr + '-01';
    },
    _buildPayload() {
      //Need to update this to reflect the actual payload
      return {
        filter: {
          startDate: this.startDate,
          endDate: this.endDate,
          billingType:
            this.selectedBillingType == 'Show All'
              ? ''
              : this.selectedBillingType,
          billingProvider: this.selectedBillingProviderList.length
            ? this.selectedBillingProviderList.map(provider => provider.value)
            : [],
          user: this.selectedUserList.length
            ? this.selectedUserList.map(user => user.id)
            : [],
          billingEntity: this.selectedBillingEntityList.length
            ? this.selectedBillingEntityList.map(entity => entity.value)
            : [],
          location: this.selectedLocationList.length
            ? this.selectedLocationList.map(location => location.value)
            : [],
          department: this.selectedDepartmentList.length
            ? this.selectedDepartmentList.map(department => department.value)
            : [],
          financialClass: this.selectedFinancialClassList.length
            ? this.selectedFinancialClassList.map(
                financialClass => financialClass.value
              )
            : [],
          //we send the whole payer-plan object to the backend
          payerPlan: this.selectedPayerPlanList,
          payer: this.selectedPayerList.length
            ? this.selectedPayerList.map(payer => payer.value)
            : [],
          plan: this.selectedPlanList.length
            ? this.selectedPlanList.map(plan => plan.plan_name || plan.value)
            : [],
          //convert balance bucket to enum value
          balanceBucket: this.selectedBalanceBucketList.length
            ? this.selectedBalanceBucketList.map(bucket => bucket.id)
            : [],
          //send individual phase, status, reason arrays
          phase: this.selectedPhaseList.length
            ? this.selectedPhaseList.map(item => item.phase)
            : [],
          status: this.selectedStatusList.length
            ? this.selectedStatusList.map(item => item.status)
            : [],
          reason: this.selectedReasonList.length
            ? this.selectedReasonList.map(item => item.reason)
            : [],
          ageCategory: this.selectedAgeCategoryList.length
            ? this.selectedAgeCategoryList.map(
                ageCategory => ageCategory.age_category
              )
            : [],
          //send individual activity type and code arrays
          activityTypeOnly: this.selectedLastActivityTypeList.length
            ? this.selectedLastActivityTypeList.map(
                item => item.activity_type_id
              )
            : [],
          activitySubtype: this.selectedLastActivityCodeList.length
            ? this.selectedLastActivityCodeList.map(
                item => item.activity_subtype_id
              )
            : [],
        },
      };
    },
    async clearFilter(filter) {
      switch (filter) {
        case 'Billing Type Applied':
          this.selectedBillingType = '';
          break;
        case 'Billing Provider Applied':
          this.selectedBillingProviderList = [];
          break;
        case 'Billing Entity Applied':
          this.selectedBillingEntityList = [];
          break;
        case 'User Applied':
          this.selectedUserList = [];
          break;
        case 'Location Applied':
          this.selectedLocationList = [];
          break;
        case 'Department Applied':
          this.selectedDepartmentList = [];
          break;
        case 'Financial Class Applied':
          this.selectedFinancialClassList = [];
          break;
        case 'Payer Plan Applied':
          this.selectedPayerPlanList = [];
          break;
        case 'Payer Applied':
          this.selectedPayerList = [];
          break;
        case 'Plan Applied':
          this.selectedPlanList = [];
          break;
        case 'Phase Applied':
          this.selectedPhaseList = [];
          break;
        case 'Status Applied':
          this.selectedStatusList = [];
          break;
        case 'Reason Applied':
          this.selectedReasonList = [];
          break;
        case 'Balance Bucket Applied':
          this.selectedBalanceBucketList = [];
          break;
        case 'Age Category Applied':
          this.selectedAgeCategoryList = [];
          break;
        case 'Activity Type Code Applied':
          this.selectedActivityTypeCodeList = [];
          break;
        case 'Activity Type Applied':
          this.selectedLastActivityTypeList = [];
          break;
        case 'Activity Code Applied':
          this.selectedLastActivityCodeList = [];
          break;
        default:
          break;
      }

      await this.applyFilters(
        this.startDate,
        this.endDate,
        this.selectedBillingType,
        this.selectedBillingProviderList,
        this.selectedBillingEntityList,
        this.selectedUserList,
        this.selectedLocationList,
        this.selectedDepartmentList,
        this.selectedFinancialClassList,
        this.selectedPayerPlanList,
        this.selectedPayerList,
        this.selectedPlanList,
        this.selectedPhaseList,
        this.selectedStatusList,
        this.selectedReasonList,
        this.selectedBalanceBucketList,
        this.selectedAgeCategoryList,
        this.selectedLastActivityTypeList,
        this.selectedLastActivityCodeList
      );
    },
  },
});
